import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  FULL_REMOTE_WORK_STYLE,
  FULLY_REMOTE_PREFERENCE,
  OFFICE_DAYS_OF_WEEK_NONE_VALUE,
} from 'spa/member/components/WorkPreference/constants'

export const useDesiredWorkStyleControl = () => {
  const { watch, getValues, setValue, clearErrors } = useFormContext()
  // 出社可能曜日の「なし」を自動設定したかどうか
  const autoSetOfficeDaysRef = useRef(false)

  const desiredWorkStyles: string[] = watch('desired_work_styles') || []
  const remoteWorkPreference = watch('remote_work_preference')

  const isFullRemoteOnly =
    desiredWorkStyles.length === 1 && desiredWorkStyles[0] === FULL_REMOTE_WORK_STYLE

  // フルリモートのみ かつ 出社が完全に不可能 のときは出社可能曜日を聞かない
  const officeDaysDisabled = isFullRemoteOnly && remoteWorkPreference === FULLY_REMOTE_PREFERENCE

  // 希望する稼働形態を回答済みなら出社の要否が決まるため、出社可能曜日の「なし」は選ばせない
  const officeDaysNoneOptionDisabled = desiredWorkStyles.length > 0

  // 希望する稼働形態が未選択、もしくはフルリモートのみのときに注釈を出す
  const showRecommendationMessage = desiredWorkStyles.length === 0 || isFullRemoteOnly

  // 読み込み時は onChange が走らないため、希望する稼働形態を操作したときだけリセットされる。
  // これによりリリース前に登録したユーザー（稼働形態が未回答）の既存回答は保持される。
  // getValues で変更後の値を読むため、react-hook-form が値を確定した後（MultiCheckBoxForm の onChange）で呼ぶこと
  const onDesiredWorkStylesChange = () => {
    const selected: string[] = getValues('desired_work_styles') || []
    const nextIsFullRemoteOnly =
      selected.length === 1 && selected[0] === FULL_REMOTE_WORK_STYLE
    const officeDays: string[] = getValues('office_days_of_week') || []

    // 希望する稼働形態を回答すると「なし」は非活性になるため、「なし」が選択済みなら解除して再選択を促す
    if (selected.length > 0 && officeDays.includes(OFFICE_DAYS_OF_WEEK_NONE_VALUE)) {
      setValue('office_days_of_week', [], { shouldValidate: true })
      autoSetOfficeDaysRef.current = false
    }

    if (nextIsFullRemoteOnly) return

    // shouldValidate を付けると、必須解除（required: false）の再registerが次のレンダーになる分だけ
    // 「この質問は必須です」が立ち、非活性の設問にエラーが残る。値を消してエラーも明示的に消す
    setValue('remote_work_preference', null)
    clearErrors('remote_work_preference')
  }

  useEffect(() => {
    if (officeDaysDisabled) {
      setValue('office_days_of_week', [OFFICE_DAYS_OF_WEEK_NONE_VALUE], { shouldValidate: true })
      autoSetOfficeDaysRef.current = true
      return
    }

    // 非活性が解除されたときは、自動設定した「なし」だけをクリアして再選択を促す
    // （ユーザー自身が回答した「なし」は消さないため、値ではなく自動設定したかどうかで判定する）
    if (autoSetOfficeDaysRef.current) {
      autoSetOfficeDaysRef.current = false
      setValue('office_days_of_week', [], { shouldValidate: true })
    }
  }, [officeDaysDisabled])

  return {
    isFullRemoteOnly,
    officeDaysDisabled,
    officeDaysNoneOptionDisabled,
    showRecommendationMessage,
    onDesiredWorkStylesChange,
  }
}
